import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import {
  ArrowLeft,
  Video,
  User,
  Clock,
  Calendar,
  CheckCircle,
  XCircle,
  AlertCircle,
  MessageSquare,
  Star,
  Loader2,
  FileText,
  Download,
  Play,
  Sparkles,
  Mail,
  ThumbsUp,
  ThumbsDown
} from "lucide-react";

const InterviewResults = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [summarizing, setSummarizing] = useState(false);
  const [error, setError] = useState(null);
  const [sendingStatus, setSendingStatus] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(null);
  const [customMessage, setCustomMessage] = useState("");

  useEffect(() => {
    if (sessionId) {
      fetchSession();
    }
  }, [sessionId]);

  const fetchSession = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/ai/interview/${sessionId}`, {
        credentials: "include",
      });
      
      if (response.ok) {
        const data = await response.json();
        setSession(data);
      } else {
        const err = await response.json();
        setError(err.detail || "Failed to load interview");
      }
    } catch (err) {
      console.error("Failed to fetch interview:", err);
      setError("Failed to load interview");
    } finally {
      setLoading(false);
    }
  };

  const generateSummary = async () => {
    setSummarizing(true);
    try {
      const response = await fetch(`${API}/ai/interview/${sessionId}/summarize`, {
        method: "POST",
        credentials: "include",
      });
      
      if (response.ok) {
        const data = await response.json();
        setSession(prev => ({ ...prev, summary: data.summary }));
        toast.success("Summary generated!");
      } else {
        toast.error("Failed to generate summary");
      }
    } catch (err) {
      toast.error("Failed to generate summary");
    } finally {
      setSummarizing(false);
    }
  };

  const downloadPDF = async () => {
    try {
      const response = await fetch(`${API}/reports/interview/${sessionId}/pdf`, {
        credentials: "include",
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `interview_${sessionId}.pdf`;
        link.click();
        URL.revokeObjectURL(url);
        toast.success("PDF downloaded!");
      } else {
        toast.error("Failed to download PDF");
      }
    } catch (err) {
      toast.error("Failed to download PDF");
    }
  };

  const sendStatusUpdate = async (statusType) => {
    setSendingStatus(statusType);
    try {
      const response = await fetch(`${API}/ai/interview/${sessionId}/send-status`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          status: statusType,
          message: customMessage || ""
        }),
      });
      
      if (response.ok) {
        const data = await response.json();
        setSession(prev => ({ ...prev, candidate_status: statusType }));
        toast.success(`${statusType === "next_stage" ? "Advancement" : "Rejection"} notification sent to ${session.candidate_name || "candidate"}!`);
        setShowStatusModal(null);
        setCustomMessage("");
      } else {
        const err = await response.json();
        toast.error(err.detail || "Failed to send notification");
      }
    } catch (err) {
      toast.error("Failed to send notification");
    } finally {
      setSendingStatus(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed": return "bg-green-500/20 text-green-400 border-green-500/30";
      case "active": return "bg-blue-500/20 text-blue-400 border-blue-500/30";
      case "timed_out": return "bg-red-500/20 text-red-400 border-red-500/30";
      default: return "bg-gray-500/20 text-gray-400 border-gray-500/30";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex">
        <Sidebar />
        <main className="flex-1 lg:ml-64 flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-crimson animate-spin" />
        </main>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="min-h-screen bg-obsidian flex">
        <Sidebar />
        <main className="flex-1 lg:ml-64 flex items-center justify-center">
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Interview Not Found</h2>
            <p className="text-gray-400 mb-6">{error || "Unable to load interview"}</p>
            <Button onClick={() => navigate(-1)} className="bg-crimson hover:bg-crimson-hover">
              Go Back
            </Button>
          </div>
        </main>
      </div>
    );
  }

  const isRecruiter = user?.role === "recruiter";

  return (
    <div className="min-h-screen bg-obsidian flex">
      <Sidebar />

      <main className="flex-1 lg:ml-64 p-4 sm:p-6 lg:p-8 pt-16 lg:pt-8">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>

          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-crimson/20 flex items-center justify-center">
                <Video className="w-6 h-6 text-crimson" />
              </div>
              <div>
                <h1 className="font-heading text-2xl font-bold">Interview Results</h1>
                <p className="text-gray-400">{session.job_title || "AI Interview"}</p>
              </div>
            </div>
            <span className={`px-4 py-2 text-sm font-medium border ${getStatusColor(session.status)}`}>
              {session.status?.replace("_", " ").toUpperCase()}
            </span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Interview Details */}
              <motion.div
                className="bg-obsidian-light border border-[#222] p-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <h2 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-crimson" />
                  Interview Details
                </h2>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-gray-500 text-sm">Candidate</p>
                    <p className="text-white font-medium">{session.candidate_name || "Unknown"}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm">Date</p>
                    <p className="text-white font-medium">
                      {session.started_at 
                        ? new Date(session.started_at).toLocaleDateString("en-US", {
                            weekday: "long",
                            year: "numeric",
                            month: "long",
                            day: "numeric"
                          })
                        : "Not started"
                      }
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm">Duration</p>
                    <p className="text-white font-medium">{session.time_limit_minutes || 30} minutes</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm">Questions</p>
                    <p className="text-white font-medium">{session.num_questions || 6} questions</p>
                  </div>
                </div>
              </motion.div>

              {/* AI Summary */}
              {isRecruiter && (
                <motion.div
                  className="bg-obsidian-light border border-[#222] p-6"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                >
                  <h2 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-crimson" />
                    AI Analysis
                  </h2>
                  
                  {session.summary ? (
                    <div className="prose prose-invert max-w-none">
                      <pre className="whitespace-pre-wrap text-gray-300 text-sm font-sans leading-relaxed bg-obsidian p-4 rounded border border-[#333]">
                        {session.summary}
                      </pre>
                    </div>
                  ) : session.status === "completed" ? (
                    <div className="text-center py-6">
                      <p className="text-gray-400 mb-4">Generate an AI summary of this interview</p>
                      <Button
                        onClick={generateSummary}
                        disabled={summarizing}
                        className="bg-crimson hover:bg-crimson-hover"
                        data-testid="generate-summary-btn"
                      >
                        {summarizing ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Analyzing...
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-4 h-4 mr-2" />
                            Generate Summary
                          </>
                        )}
                      </Button>
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4">
                      Summary will be available when the interview is completed
                    </p>
                  )}
                </motion.div>
              )}

              {/* Transcript */}
              <motion.div
                className="bg-obsidian-light border border-[#222] p-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <h2 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-crimson" />
                  Interview Transcript
                </h2>
                
                {session.messages?.length > 0 ? (
                  <div className="space-y-4 max-h-[500px] overflow-y-auto">
                    {session.messages.map((msg, idx) => (
                      <div
                        key={idx}
                        className={`p-4 border-l-2 ${
                          msg.role === "assistant" 
                            ? "border-crimson bg-crimson/5" 
                            : "border-blue-500 bg-blue-500/5"
                        }`}
                      >
                        <p className={`text-xs font-bold mb-2 ${
                          msg.role === "assistant" ? "text-crimson" : "text-blue-400"
                        }`}>
                          {msg.role === "assistant" ? "AI INTERVIEWER" : "CANDIDATE"}
                        </p>
                        <p className="text-gray-300 text-sm whitespace-pre-wrap">{msg.content}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">No transcript available</p>
                )}
              </motion.div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Candidate Decision */}
              {isRecruiter && session.status === "completed" && (
                <motion.div
                  className="bg-obsidian-light border border-[#222] p-6"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                >
                  <h3 className="font-heading text-lg font-bold mb-4">Candidate Decision</h3>
                  
                  {session.candidate_status ? (
                    <div className={`p-4 border ${session.candidate_status === "next_stage" ? "border-green-500/30 bg-green-500/10" : "border-red-500/30 bg-red-500/10"}`}>
                      <p className={`text-sm font-medium ${session.candidate_status === "next_stage" ? "text-green-400" : "text-red-400"}`}>
                        {session.candidate_status === "next_stage" ? "✓ Advanced to Next Stage" : "✗ Rejected"}
                      </p>
                      <p className="text-gray-500 text-xs mt-1">Decision sent to candidate</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <Button
                        onClick={() => setShowStatusModal("next_stage")}
                        className="w-full bg-green-600 hover:bg-green-700 text-white"
                        data-testid="advance-candidate-btn"
                      >
                        <ThumbsUp className="w-4 h-4 mr-2" />
                        Advance to Next Stage
                      </Button>
                      <Button
                        onClick={() => setShowStatusModal("rejected")}
                        variant="outline"
                        className="w-full border-red-500/50 text-red-400 hover:bg-red-500/10"
                        data-testid="reject-candidate-btn"
                      >
                        <ThumbsDown className="w-4 h-4 mr-2" />
                        Send Rejection
                      </Button>
                    </div>
                  )}
                </motion.div>
              )}

              {/* Actions */}
              <motion.div
                className="bg-obsidian-light border border-[#222] p-6"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.05 }}
              >
                <h3 className="font-heading text-lg font-bold mb-4">Actions</h3>
                <div className="space-y-3">
                  <Button
                    onClick={downloadPDF}
                    className="w-full bg-crimson hover:bg-crimson-hover"
                    data-testid="download-pdf-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download PDF
                  </Button>
                  
                  {isRecruiter && session.candidate_id && (
                    <Button
                      onClick={() => navigate(`/candidates/${session.candidate_id}`)}
                      variant="outline"
                      className="w-full border-[#333] text-white"
                    >
                      <User className="w-4 h-4 mr-2" />
                      View Profile
                    </Button>
                  )}
                  
                  {isRecruiter && session.candidate_id && (
                    <Button
                      onClick={() => navigate(`/messages?to=${session.candidate_id}&name=${encodeURIComponent(session.candidate_name || "Candidate")}`)}
                      variant="outline"
                      className="w-full border-[#333] text-white"
                    >
                      <MessageSquare className="w-4 h-4 mr-2" />
                      Send Message
                    </Button>
                  )}
                </div>
              </motion.div>

              {/* Quick Stats */}
              <motion.div
                className="bg-obsidian-light border border-[#222] p-6"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
              >
                <h3 className="font-heading text-lg font-bold mb-4">Quick Stats</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Questions Asked</span>
                    <span className="text-white font-medium">
                      {session.messages?.filter(m => m.role === "assistant").length || 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Responses</span>
                    <span className="text-white font-medium">
                      {session.messages?.filter(m => m.role === "user").length || 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Status</span>
                    <span className={session.status === "completed" ? "text-green-400" : "text-yellow-400"}>
                      {session.status?.replace("_", " ")}
                    </span>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Status Modal */}
        {showStatusModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <motion.div
              className="bg-obsidian-light border border-[#222] p-6 max-w-md w-full"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              <h3 className="font-heading text-xl font-bold mb-4">
                {showStatusModal === "next_stage" ? "Advance Candidate" : "Send Rejection"}
              </h3>
              <p className="text-gray-400 mb-4">
                {showStatusModal === "next_stage" 
                  ? `Send an advancement notification to ${session.candidate_name || "the candidate"}. They will receive both an in-app message and email.`
                  : `Send a rejection notification to ${session.candidate_name || "the candidate"}. They will receive both an in-app message and email.`
                }
              </p>
              
              <div className="mb-4">
                <label className="text-sm text-gray-400 mb-2 block">Custom Message (optional)</label>
                <textarea
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}
                  placeholder="Add a personalized message..."
                  rows={4}
                  className="w-full p-3 bg-obsidian border border-[#333] text-white placeholder-gray-500 resize-none focus:border-crimson focus:outline-none"
                />
              </div>
              
              <div className="flex gap-3">
                <Button
                  onClick={() => {
                    setShowStatusModal(null);
                    setCustomMessage("");
                  }}
                  variant="outline"
                  className="flex-1 border-[#333] text-white"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => sendStatusUpdate(showStatusModal)}
                  disabled={sendingStatus}
                  className={`flex-1 ${showStatusModal === "next_stage" ? "bg-green-600 hover:bg-green-700" : "bg-red-600 hover:bg-red-700"}`}
                >
                  {sendingStatus ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : showStatusModal === "next_stage" ? (
                    <ThumbsUp className="w-4 h-4 mr-2" />
                  ) : (
                    <ThumbsDown className="w-4 h-4 mr-2" />
                  )}
                  {sendingStatus ? "Sending..." : "Send Notification"}
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </main>
    </div>
  );
};

export default InterviewResults;
